import { useState, useEffect, useRef } from 'react';

// Sayıyı Türkçe metin olarak yaz (ör: 1500,50 → "bin beş yüz TL elli kuruş")
const numberToText = (num) => {
  if (!num || num === 0) return '';
  const birler = ['', 'bir', 'iki', 'üç', 'dört', 'beş', 'altı', 'yedi', 'sekiz', 'dokuz'];
  const onlar = ['', 'on', 'yirmi', 'otuz', 'kırk', 'elli', 'altmış', 'yetmiş', 'seksen', 'doksan'];

  const convertGroup = (n) => {
    if (n === 0) return '';
    let result = '';
    const yuzler = Math.floor(n / 100);
    const kalan = n % 100;
    const onlarDigit = Math.floor(kalan / 10);
    const birlerDigit = kalan % 10;
    if (yuzler > 0) result += (yuzler === 1 ? '' : birler[yuzler]) + 'yüz';
    if (onlarDigit > 0) result += onlar[onlarDigit];
    if (birlerDigit > 0) result += birler[birlerDigit];
    return result;
  };

  const abs = Math.abs(num);
  const intPart = Math.floor(abs);
  const decPart = Math.round((abs - intPart) * 100);

  if (intPart === 0 && decPart === 0) return '';

  let text = '';

  if (intPart > 0) {
    const milyar = Math.floor(intPart / 1000000000);
    const milyon = Math.floor((intPart % 1000000000) / 1000000);
    const bin = Math.floor((intPart % 1000000) / 1000);
    const kalan = intPart % 1000;

    if (milyar > 0) text += convertGroup(milyar) + 'milyar';
    if (milyon > 0) text += convertGroup(milyon) + 'milyon';
    if (bin > 0) text += (bin === 1 ? '' : convertGroup(bin)) + 'bin';
    if (kalan > 0) text += convertGroup(kalan);

  }

  if (decPart > 0) {
    if (intPart > 0) text += ',';
    text += convertGroup(decPart);
  }

  return (num < 0 ? 'eksi ' : '') + text;
};

// Raw string (binlik ayracı olmadan, virgüllü): "7000,50" → number
const rawToNumber = (raw) => {
  if (!raw) return 0;
  return parseFloat(raw.replace(',', '.')) || 0;
};

// Number → raw string (binlik ayracı olmadan)
const numberToRaw = (num) => {
  if (!num && num !== 0) return '';
  if (num === 0) return '';
  const abs = Math.abs(num);
  const fixed = abs.toFixed(2);
  const [intStr, decStr] = fixed.split('.');
  if (decStr === '00') return intStr;
  if (decStr.endsWith('0')) return `${intStr},${decStr[0]}`;
  return `${intStr},${decStr}`;
};

// Format raw string → display string (binlik noktalarıyla): "7000,50" → "7.000,50"
const formatDisplay = (raw) => {
  if (!raw) return '';
  const parts = raw.split(',');
  const intPart = parts[0] || '0';
  const decPart = parts.length > 1 ? parts[1] : null;
  const withDots = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return decPart !== null ? `${withDots},${decPart}` : withDots;
};

// Display string → raw string (binlik noktalarını kaldır): "7.000,50" → "7000,50"
const displayToRaw = (display) => {
  if (!display) return '';
  return display.replace(/\./g, '');
};

const PriceInput = ({ value, onChange, placeholder, className, style, disabled, autoFocus, min, max, onKeyDown: externalKeyDown, hideText, ...props }) => {
  const [raw, setRaw] = useState('');
  const inputRef = useRef(null);
  const skipNextSync = useRef(false);
  const cursorRestore = useRef(null);

  useEffect(() => {
    if (skipNextSync.current) {
      skipNextSync.current = false;
      return;
    }
    setRaw(numberToRaw(parseFloat(value) || 0));
  }, [value]);

  // İmleç pozisyonunu formatlamadan sonra doğru yere geri koy
  useEffect(() => {
    if (cursorRestore.current !== null && inputRef.current) {
      const pos = cursorRestore.current;
      inputRef.current.setSelectionRange(pos, pos);
      cursorRestore.current = null;
    }
  });

  const handleChange = (e) => {
    const input = e.target;
    const cursorPos = input.selectionStart;
    const oldDisplay = formatDisplay(raw);
    let newDisplay = input.value;

    // Noktayı (.) virgüle çevir, birden fazla virgülü engelle
    newDisplay = newDisplay.replace(/\./g, (match, offset) => {
      // Binlik ayracı olan noktaları koru (sadece ilk virgül olmayan noktayı dönüştür)
      return match;
    });

    // Display'den raw'a çevir (binlik noktalarını kaldır)
    let newRaw = displayToRaw(newDisplay);

    // Sadece rakam ve virgül (ondalık ayracı) bırak
    newRaw = newRaw.replace(/[^\d,]/g, '');

    // Birden fazla virgül varsa sadece ilkini tut
    const commaIdx = newRaw.indexOf(',');
    if (commaIdx !== -1) {
      newRaw = newRaw.slice(0, commaIdx + 1) + newRaw.slice(commaIdx + 1).replace(/,/g, '');
    }

    // Ondalık kısım max 2 hane
    if (newRaw.includes(',')) {
      const [intStr, decStr] = newRaw.split(',');
      newRaw = intStr + ',' + (decStr || '').slice(0, 2);
    }

    // Başta gereksiz sıfırları temizle (ör: "007" → "7", ama "0" ve "0,5" olduğu gibi kalsın)
    if (newRaw.includes(',')) {
      const [intStr, decStr] = newRaw.split(',');
      const cleanedInt = intStr.replace(/^0+/, '') || '0';
      newRaw = cleanedInt + ',' + decStr;
    } else if (newRaw.length > 1) {
      newRaw = newRaw.replace(/^0+/, '') || '';
    }

    // Max değer kontrolü
    if (rawToNumber(newRaw) > 9999999999.99) return;

    // İmleç pozisyonunu hesapla
    const newFormatted = formatDisplay(newRaw);
    const oldLen = oldDisplay.length;
    const newLen = newFormatted.length;
    const lenDiff = newLen - oldLen;
    // İmleç farkı: eklenen/silinen karakter sayısını hesaba kat
    let newCursor = cursorPos + (newLen - newDisplay.length);
    if (newCursor < 0) newCursor = 0;
    if (newCursor > newFormatted.length) newCursor = newFormatted.length;

    cursorRestore.current = newCursor;
    setRaw(newRaw);
    skipNextSync.current = true;
    onChange(rawToNumber(newRaw));
  };

  const handleKeyDown = (e) => {
    // Nokta ve virgülü ondalık ayracı olarak kullan
    if (e.key === '.' || e.key === ',') {
      if (raw.includes(',')) {
        e.preventDefault();
        return;
      }
      // Noktayı virgüle çevir
      if (e.key === '.') {
        e.preventDefault();
        const input = e.target;
        const pos = input.selectionStart;
        const display = input.value;
        const newDisplay = display.slice(0, pos) + ',' + display.slice(pos);
        // Simulate change
        const fakeEvent = { target: { value: newDisplay, selectionStart: pos + 1 } };
        handleChange(fakeEvent);
        cursorRestore.current = pos + 1;
        return;
      }
    }

    // Harfleri engelle (Ctrl+A, Ctrl+C gibi kısayollar hariç)
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !/[\d,.]/.test(e.key)) {
      e.preventDefault();
      return;
    }

    if (externalKeyDown) externalKeyDown(e);
  };

  const numericValue = rawToNumber(raw);
  const textRepresentation = numberToText(numericValue);

  return (
    <div style={{ width: '100%' }}>
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={formatDisplay(raw)}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || '0'}
        className={className}
        style={style}
        disabled={disabled}
        autoFocus={autoFocus}
        {...props}
      />
      {!hideText && textRepresentation && (
        <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '3px', fontStyle: 'italic', letterSpacing: '0.3px' }}>
          #{textRepresentation}#
        </div>
      )}
    </div>
  );
};

export { numberToText };
export default PriceInput;
